import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { CapturumDialogService } from '@capturum/ui/api';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { filter } from 'rxjs';

/**
 * Closes any open overlays (dynamic dialogs, confirm dialog) when the router
 * starts navigating, so a popup never lingers on top of the next page after a
 * menu click. Open/interact/explicit-close/backdrop behavior is untouched —
 * this only reacts to NavigationStart.
 */
@Injectable({ providedIn: 'root' })
export class OverlayDismissService {
  readonly #router = inject(Router);
  readonly #dialogService = inject(DialogService);
  readonly #confirmationService = inject(ConfirmationService);
  // Root app config does not provide CapturumDialogService, so resolve it
  // optionally; if a root instance exists its dialogs are closed too.
  readonly #capturumDialogService = inject(CapturumDialogService, { optional: true });
  readonly #destroyRef = inject(DestroyRef);

  #initialized = false;

  /**
   * Starts listening for navigation. Idempotent — safe to call from the app
   * shell on every bootstrap; only the first call wires up the subscription.
   */
  init(): void {
    if (this.#initialized === true) {
      return;
    }

    this.#initialized = true;

    this.#router.events
      .pipe(
        filter((event) => {
          return event instanceof NavigationStart;
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.closeAll();
      });
  }

  /**
   * Closes every currently open dynamic dialog and the confirm dialog.
   */
  closeAll(): void {
    this.#closeDynamicDialogs(this.#dialogService);

    if (this.#capturumDialogService !== null) {
      this.#closeDynamicDialogs(this.#capturumDialogService);
    }

    this.#confirmationService.close();
  }

  #closeDynamicDialogs(dialogService: DialogService | CapturumDialogService): void {
    // Snapshot the refs first: ref.close() mutates dialogComponentRefMap, so
    // iterating the live map keys while closing would skip entries.
    const openDialogRefs = Array.from(dialogService.dialogComponentRefMap.keys());

    for (const dialogRef of openDialogRefs) {
      dialogRef.close();
    }
  }
}
